import React, { useState, useContext } from 'react';
import moment from 'moment';
import CurrentCustomer from 'context/global-customer-creation/context';
import type { DataCurrentCustomerType } from 'context/global-customer-creation/types';

import {
  createMoralPersonalData,
  updateMoralPersonalData,
  saveMoralLegalRepresentative,
  createMoralEconomicInformation,
  updateMoralEconomicInformation,
  createMoralBankAccount,
  updateMoralBankAccount,
} from 'api/client';

import { getDateByObject } from 'utils/date';
import OnboardingSaveSteps from './context';
import type {
  BankAccountValues,
  EconomicInformationValues,
  GeneralDataValues,
  LegalRepresentativeValues,
} from './models';

const initialState = { saved: false };
const initialStepPersonalState = {
  clientId: null,
  contractId: null,
  name: '',
  fathersLastName: '',
  mothersLastName: '',
  fullName: '',
  saved: false,
};

interface Props {
  children: React.ReactNode;
}

const OnboardingSaveStepsProvider = ({ children }: Props) => {
  const { updateCurrentCustomer } = useContext(CurrentCustomer);
  const [dataStepPersonal, setDataStepPersonal] = useState<GeneralDataValues>(
    initialStepPersonalState as GeneralDataValues,
  );
  const [dataStepEconomicInfo, setDataStepEconomicInfo] =
    useState<EconomicInformationValues>(initialState);
  const [dataStepAccount, setDataStepAccount] = useState<BankAccountValues>(
    initialState as BankAccountValues,
  );
  const [dataStepLegalRepresentative, setDataStepLegalRepresentative] =
    useState<LegalRepresentativeValues>(
      initialState as LegalRepresentativeValues,
    );
  const [zipCodes, setZipCodes] = useState<string[]>([]);
  const [fiscalZipCodes, setFiscalZipCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const resetMoralPersonSteps = () => {
    setDataStepPersonal(initialStepPersonalState as GeneralDataValues);
    setDataStepEconomicInfo(initialState);
    setDataStepAccount(initialState as BankAccountValues);
    setDataStepLegalRepresentative(initialState as LegalRepresentativeValues);
  };

  const assignEmptyString = (obj: any) =>
    Object.keys(obj).forEach((key) => {
      obj[key] = obj[key] || '';
    });

  const getBankAccountInitialValues = (fields: any) => ({
    bankAccounts: [
      {
        [fields.bank.name]: '',
        [fields.clabe.name]: '',
        [fields.branchOffice.name]: '',
        [fields.plaza.name]: '',
      },
    ],
  });

  const personalDataRemapper = (values: GeneralDataValues) => {
    // Addresses

    let homeAddressId = null;
    let foreignAddressId = null;
    let fiscalAddressId = null;

    if (dataStepPersonal.saved && dataStepPersonal.address)
      dataStepPersonal.addresses.forEach((el: any) => {
        if (el.addressType === 'HOME_ADDRESS') homeAddressId = el.addressId;
        if (el.addressType === 'FOREIGN_ADDRESS')
          foreignAddressId = el.addressId;
        if (el.addressType === 'FISCAL_ADDRESS') fiscalAddressId = el.addressId;
      });

    const homeAddress = {
      addressId: homeAddressId,
      areaStreet: values.areaStreet,
      areaExteriorNumber: values.areaExteriorNumber,
      areaInteriorNumber: values.areaInteriorNumber,
      zipCode: values.zipCode,
      areaSuburb: values.areaSuburb && values.areaSuburb.label,
      cveLocation: values.cveLocation,
      areaCity: values.areaCity,
      areaState: values.areaState,
      cveState: values.areaState,
      cvePais: values.cvePais && values.cvePais.label,
      addressType: 'HOME_ADDRESS',
    };

    const foreignAddress = {
      addressId: foreignAddressId,
      areaStreet: values.foreignAreaStreet,
      areaState: values.foreignAreaState && values.foreignAreaState.label,
      cveState: values.foreignAreaState && values.foreignAreaState.value,
      cvePais: values.foreignCvePais && values.foreignCvePais.label,
      addressType: 'FOREIGN_ADDRESS',
    };

    const fiscalAddress = {
      addressId: fiscalAddressId,
      areaStreet: values.fiscalAreaStreet,
      areaExteriorNumber: values.fiscalAreaExteriorNumber,
      areaInteriorNumber: values.fiscalAreaInteriorNumber,
      zipCode: values.fiscalZipCode,
      areaSuburb: values.fiscalAreaSuburb && values.fiscalAreaSuburb.label,
      cveLocation: values.fiscalCveLocation,
      areaCity: values.fiscalAreaCity,
      areaState: values.fiscalAreaState,
      cveState: values.fiscalAreaState,
      cvePais: values.fiscalCvePais && values.fiscalCvePais.label,
      addressType: 'FISCAL_ADDRESS',
    };

    // Documents
    let companyActId = null;
    let powerActId = null;
    let reformActId = null;
    let assemblyActId = null;

    if (dataStepPersonal.saved)
      dataStepPersonal.documents.forEach((el: any) => {
        if (el.cveTipoDocumento === 'COMPANY_ACT') companyActId = el.documentId;
        if (el.cveTipoDocumento === 'POWER_ACT') powerActId = el.documentId;
        if (el.cveTipoDocumento === 'REFORM_ACT') reformActId = el.documentId;
        if (el.cveTipoDocumento === 'ASSEMBLY_ACT')
          assemblyActId = el.documentId;
      });

    const companyAct = {
      documentId: companyActId,
      cveTipoDocumento: 'COMPANY_ACT',
      numEscrituraPublica: values.companyActNumber || '',
      fecRpc: moment(values.companyActDate).format('YYYY/MM/DD') || '',
      cvePlazaNotarial: values.companyActPlaza || '',
      notaryName: values.companyActNotary || '',
      numNotaria: values.companyActNotaryNumber || '',
      numRpc: values.companyActPublicRecord || '',
      cvePlazaRpc: values.companyActPlazaRecord || '',
      numFolio: values.companyActFolio || '',
    };

    const powerAct: any = {
      documentId: powerActId,
      cveTipoDocumento: 'POWER_ACT',
      numEscrituraPublica: values.powerActNumber || '',
      fecRpc: moment(values.powerActDate).format('YYYY/MM/DD') || '',
      cvePlazaNotarial: values.powerActPlaza || '',
      notaryName: values.powerActNotary || '',
      numNotaria: values.powerActNotaryNumber || '',
      numRpc: values.powerActPublicRecord || '',
      cvePlazaRpc: values.powerActPlazaRecord || '',
      numFolio: values.powerActFolio || '',
      attorneyName: values.powerActRepresentativeName || '',
      attorneyFathersLastName:
        values.powerActRepresentativeFathersLastName || '',
      attorneyMothersLastName:
        values.powerActRepresentativeMothersLastName || '',
    };

    const reformAct = {
      documentId: reformActId,
      cveTipoDocumento: 'REFORM_ACT',
      numEscrituraPublica: values.reformActNumber || '',
      fecRpc: moment(values.reformActDate).format('YYYY/MM/DD') || '',
      cvePlazaNotarial: values.reformActPlaza || '',
      notaryName: values.reformActNotary || '',
      numNotaria: values.reformActNotaryNumber || '',
      numRpc: values.reformActPublicRecord || '',
      cvePlazaRpc: values.reformActPlazaRecord || '',
      numFolio: values.reformActFolio || '',
    };

    const assemblyAct = {
      documentId: assemblyActId,
      cveTipoDocumento: 'ASSEMBLY_ACT',
      numEscrituraPublica: values.assemblyActNumber || '',
      fecRpc: moment(values.assemblyActDate).format('YYYY/MM/DD') || '',
      cvePlazaNotarial: values.assemblyActPlaza || '',
      notaryName: values.assemblyActNotary || '',
      numNotaria: values.assemblyActNotaryNumber || '',
      numRpc: values.assemblyActPublicRecord || '',
      cvePlazaRpc: values.assemblyActPlazaRecord || '',
      numFolio: values.assemblyActFolio || '',
    };

    // Governances
    let governanceDirectorId = null;
    let governanceAlternateId = null;
    let governanceMemberId: any = null;

    if (dataStepPersonal.saved)
      dataStepPersonal.governances.forEach((el: any) => {
        if (el.category === 'DIRECTOR')
          governanceDirectorId = el.corporateStructureId;
        if (el.category === 'ALTERNATE')
          governanceAlternateId = el.corporateStructureId;
        if (el.category === 'MEMBER')
          governanceMemberId = el.corporateStructureId;
      });

    const director = {
      corporateStructureId: governanceDirectorId,
      category: 'DIRECTOR',
      name: values.directorsName || '',
      fathersLastName: values.directorsFathersLastName || '',
      mothersLastName: values.directorsMothersLastName || '',
      birthDate: moment(values.directorsBirthDate).format('YYYY/MM/DD') || '',
      nationality:
        (values.directorsNationality && values.directorsNationality.label) ||
        '',
      rfc: values.directorsRfc || '',
    };

    const alternate = {
      corporateStructureId: governanceAlternateId,
      category: 'ALTERNATE',
      name: values.alternateName || '',
      fathersLastName: values.alternateFathersLastName || '',
      mothersLastName: values.alternateMothersLastName || '',
      birthDate: moment(values.alternateBirthDate).format('YYYY/MM/DD') || '',
      nationality:
        (values.alternateNationality && values.alternateNationality.label) ||
        '',
      rfc: values.alternateRfc || '',
      position: values.alternatePosition || '',
    };

    const members = values.members.map((item: any) => ({
      corporateStructureId: governanceMemberId,
      category: 'MEMBER',
      name: item.memberName || '',
      fathersLastName: item.memberFathersLastName || '',
      mothersLastName: item.memberMothersLastName || '',
      birthDate: moment(item.memberBirthDate).format('YYYY/MM/DD') || '',
      nationality: item.memberNationality || '',
      rfc: item.memberRfc || '',
      position: item.memberPosition || '',
    }));

    // Shareholders
    const shareholders = () => {
      return values.shareholders.map((item: any) => ({
        ...item,
        shareHoldingStructureId: item.shareHoldingStructureId || null,
        personType: item.personType && item.personType.label,
      }));
    };

    return {
      clientId: dataStepPersonal.clientId,
      clientRole: 'CL',
      cveInternal: values.cveInternal || '',
      rfc: values.rfc,
      nationality: values.nationality && values.nationality.label,
      name: values.name || '',
      moralPersonType: values.moralPersonType && values.moralPersonType.label,
      societyType: values.societyType || '',
      companyPhone: values.companyPhone || '',
      companyWeb: values.companyWeb || '',
      companyEmail: values.companyEmail || '',
      addresses: [homeAddress, fiscalAddress, foreignAddress],
      documents: [companyAct, powerAct, reformAct, assemblyAct],
      shareholders: shareholders(),
      governances: [director, alternate, ...members],
      openingDate: getDateByObject({
        date: values.openingDate,
        format: 'yyyy-MM-dd',
      }),
      cancellationDate: getDateByObject({
        date: values.cancellationDate,
        format: 'yyyy-MM-dd',
      }),
      cveTypeClient: values.cveTypeClient?.value || null,
    };
  };

  const legalRepresentativeDataRemapper = (
    values: LegalRepresentativeValues,
  ) => {
    const {
      contactName,
      contactFathersLastName,
      contactMothersLastName,
      contactGender,
      contactRfc,
      contactCurrentPosition,
      contactCompanyPhone,
      contactCellPhone,
      contactEmail1,
    } = values;

    return {
      clientId: dataStepPersonal.clientId,
      contractId: dataStepPersonal.contractId,
      legalRepresentatives: values.legalRepresentatives.map(
        (
          {
            clientId,
            rfc,
            curp,
            birthDate,
            name,
            fathersLastName,
            mothersLastName,
            gender,
            birthCountry,
            birthState,
            nationality,
            profession,
            currentPosition,
            cellPhone,
            homePhone,
            companyPhone,
            email1,
            publicDeedNum,
            notary,
            signatureType,
            areaStreet,
            areaExteriorNumber,
            areaInteriorNumber,
            zipCode,
            areaSuburb,
            cveLocation,
            areaCity,
            areaState,
            cvePais,
          }: any,
          index: number,
        ) => ({
          clientId: clientId || null,
          rfc,
          curp,
          birthDate: moment(birthDate).format('YYYY/MM/DD'),
          name,
          fathersLastName,
          mothersLastName,
          gender: gender && gender.value,
          birthCountry: birthCountry && birthCountry.value,
          birthState: birthState && birthState.value,
          nationality: nationality && nationality.value,
          profession: profession && profession.value,
          currentPosition,
          cellPhone,
          homePhone,
          companyPhone,
          email1,
          publicDeedNum,
          notary,
          signatureType: signatureType && signatureType.value,
          address: {
            addressId:
              dataStepLegalRepresentative.legalRepresentatives?.[index]?.address
                ?.addressId ?? null,
            areaStreet,
            areaExteriorNumber,
            areaInteriorNumber,
            zipCode,
            areaSuburb,
            cveLocation,
            areaCity,
            areaState: areaState && areaState.value,
            cvePais: cvePais && cvePais.value,
          },
        }),
      ),
      contact: {
        clientId: dataStepLegalRepresentative.contact.clientId || null,
        name: contactName || '',
        fathersLastName: contactFathersLastName || '',
        mothersLastName: contactMothersLastName || '',
        gender: contactGender && contactGender.value,
        rfc: contactRfc || '',
        currentPosition: contactCurrentPosition || '',
        companyPhone: contactCompanyPhone || '',
        cellPhone: contactCellPhone || '',
        email1: contactEmail1 || '',
      },
    };
  };

  const cleanValue = (value: number | string | null | undefined) => {
    if (!value) return '';
    return `${value}`.replace('$', '').replaceAll(',', '');
  };
  const economicInfoDataRemapper = (values: EconomicInformationValues) => {
    assignEmptyString(values);
    return {
      clientId: dataStepPersonal.clientId,
      ...values,
      activity: values.activity && values.activity.label,
      sector: values.sector && values.sector.label,
      taxRegime: values.taxRegime && values.taxRegime.label,
      activesTotal: cleanValue(values.activesTotal),
      anualSales: cleanValue(values.anualSales),
      pasivesTotal: cleanValue(values.pasivesTotal),
    };
  };

  const bankAccountRemapper = (values: BankAccountValues) => {
    return {
      clientId: dataStepPersonal.clientId,
      contractId: dataStepPersonal.contractId,
      bankAccounts: values.bankAccounts.map((item: any) => ({
        ...item,
        bank: (item.bank || '') && item.bank.label,
        clabe: item.clabe || '',
        plaza: item.plaza || '',
        branchOffice: item.branchOffice || '',
      })),
    };
  };

  const saveStepPersonalData = async (values: GeneralDataValues) => {
    const finalValues = personalDataRemapper(values);

    const { data, success } = dataStepPersonal.saved
      ? await updateMoralPersonalData(finalValues)
      : await createMoralPersonalData(finalValues);

    if (success && data) {
      setDataStepPersonal({
        ...finalValues,
        clientId: data.clientId,
        contractId: data.contractId,
        saved: true,
      } as any);
      updateCurrentCustomer({ ...data } as unknown as DataCurrentCustomerType);
      return { success };
    }
    return { success: false };
  };

  const saveStepLegalRepresentativeData = async (
    values: LegalRepresentativeValues,
  ) => {
    const finalValues = legalRepresentativeDataRemapper(values);

    const { data, success } =
      await await saveMoralLegalRepresentative(finalValues);

    if (success && data) {
      setDataStepLegalRepresentative({
        ...finalValues,
        saved: true,
      } as any as LegalRepresentativeValues);
      updateCurrentCustomer({ ...data } as unknown as DataCurrentCustomerType);
      return { success };
    }
    return { success: false };
  };

  const saveStepEconomicInfo = async (values: EconomicInformationValues) => {
    const finalValues = economicInfoDataRemapper(values);

    const saveStepResponse = dataStepEconomicInfo.saved
      ? await updateMoralEconomicInformation(finalValues)
      : await createMoralEconomicInformation(finalValues);

    if (saveStepResponse.success) {
      setDataStepEconomicInfo({
        ...finalValues,
        ...saveStepResponse,
        saved: true,
      } as EconomicInformationValues);
      return { success: true };
    }
    return { success: false };
  };

  const saveStepBankAccount = async (values: BankAccountValues) => {
    const finalValues = bankAccountRemapper(values);

    const saveStepResponse = dataStepAccount.saved
      ? await updateMoralBankAccount(finalValues)
      : await createMoralBankAccount(finalValues);

    if (saveStepResponse.success) {
      setDataStepAccount({ ...finalValues, ...saveStepResponse, saved: true });
      updateCurrentCustomer({
        onboardingComplete: true,
      } as DataCurrentCustomerType);
      return { success: true };
    }
    return { success: false, error: saveStepResponse };
  };

  return (
    <OnboardingSaveSteps.Provider
      value={{
        zipCodes,
        fiscalZipCodes,
        dataStepPersonal,
        dataStepEconomicInfo,
        dataStepAccount,
        dataStepLegalRepresentative,
        loading,
        setLoading,
        setZipCodes,
        setFiscalZipCodes,
        saveStepPersonalData,
        saveStepEconomicInfo,
        saveStepBankAccount,
        saveStepLegalRepresentativeData,
        setDataStepPersonal,
        setDataStepLegalRepresentative,
        setDataStepEconomicInfo,
        setDataStepAccount,
        getBankAccountInitialValues,
        resetMoralPersonSteps,
      }}
    >
      {children}
    </OnboardingSaveSteps.Provider>
  );
};

export default OnboardingSaveStepsProvider;
